/**
 * Expected AI Parser Response Structure for QUOTES (from the backend prompt).
 * This mirrors the invoice parser (see InvoiceDataAdapter) but:
 *   - carries proposal-level totals (`totalBeforeTax` / `totalAfterTax`) instead
 *     of `invoiceTotalBeforeTax` / `invoiceTotalAfterTax`,
 *   - uses a single `tax` number (quotes do not have tax line items),
 *   - adds an `incurred` line-item category, and
 *   - has no invoice_number / invoice_date / payment_terms.
 *
 * {
 *   "currency": "USD",
 *   "totalAfterTax": 600,
 *   "tax": 0,
 *   "discount": 0,
 *   "discount_percentage": 0,
 *   "totalBeforeTax": 600,
 *   "scope": "...",
 *   "line_items": [
 *     {
 *       "description": "Labor",
 *       "quantity": 2,
 *       "unit_price": 127.5,
 *       "extended_price": 255,
 *       "extended_price_after_discount": 255,
 *       "discount_amount": 0,
 *       "category": "labor"   // labor | part | equipment | travel | freight | incurred_cost | other
 *     }
 *   ]
 * }
 */

import { nullSafeGetOrElse } from './DataAccessUtils';
import { getCurrencyDecimals } from './InvoiceDataAdapter';

// Constants
const CONSTANTS = {
	DEFAULT_STATUS: 'draft',
	DEFAULT_UNIT_TYPE: 'each',
	DEFAULT_NUM_TECH: 1,
	SECONDS_PER_HOUR: 3600,
	DEFAULT_DECIMALS: 2,
	EQUIPMENT_KEYWORDS: ['equipment', 'machine', 'unit', 'system', 'hvac', 'motor', 'compressor'],
};

// Helper Functions using existing project utilities
const parseNumber = (value: any, defaultValue = 0) => parseFloat(value) || defaultValue;

const parseAmount = (value: any, defaultValue = 0, decimals: number = 2): number =>
	Number(parseNumber(value, defaultValue).toFixed(decimals));

// Get final amount with discount fallback
const getFinalAmount = (item: any): number => {
	return item.extended_price_after_discount != null
		? parseNumber(item.extended_price_after_discount)
		: parseNumber(item.extended_price);
};

// Calculate total from line items with currency-specific decimals
const calculateTotal = (lineItems: any[], decimals: number = 2): string =>
	lineItems.reduce((sum, item) => sum + parseFloat(item.amount), 0).toFixed(decimals);

// Check if description indicates equipment
const isEquipmentDescription = (description: string): boolean => {
	if (!description) return false;
	return CONSTANTS.EQUIPMENT_KEYWORDS.some((keyword) =>
		description.toLowerCase().includes(keyword)
	);
};

// Generate temporary ID for materials (negative so the backend treats them as new)
let tempIdCounter = -1;
const getNextTempId = () => tempIdCounter--;

/**
 * Convert AI parser output into the formData structure consumed by
 * InvoiceFormBase (formType: 'proposal'). Line-item field names match the
 * CostSection field keys (`laborLineItems`, `materialLineItems`,
 * `travelLineItems`, `freightLineItems`, `miscLineItems`, `incurredLineItems`),
 * and proposal-native total/tax fields are set for the payload builder + the
 * parsed-vs-final comparison.
 */
// Buyer setting "NTE based checks should be done without tax": the buyer doesn't accept tax
// on quotes, so the quote form zeroes and locks it.
export const isTaxExcludedFromQuotesByBuyer = (workOrder: object | null | undefined): boolean =>
	nullSafeGetOrElse(
		'buyerCompany.buyerCompanySettings.config.invoiceConfig.nteCheckWithoutTax',
		workOrder,
		false
	) === true;

interface QuoteFormTotals {
	invoiceTotalBeforeTax?: number;
	totalBeforeTax?: string;
}

export const excludeTaxFromQuoteFormData = <T extends QuoteFormTotals>(formData: T) => ({
	...formData,
	invoiceTax: 0,
	tax: 0,
	taxRate: '0',
	invoiceTotalAfterTax: formData.invoiceTotalBeforeTax,
	totalAfterTax: formData.totalBeforeTax,
});

export const adaptParsedQuoteToFormData = (parsedData: any, workOrder: any) => {
	if (!parsedData || !workOrder) return null;

	const currencyId = parsedData.currency || 'USD';
	const decimals = getCurrencyDecimals(currencyId);
	const initialTotal = (0).toFixed(decimals);

	const totalTax = parseNumber(parsedData.tax);
	const totalBeforeTax = parseAmount(parsedData.totalBeforeTax, 0, decimals);
	const totalAfterTax = parseAmount(parsedData.totalAfterTax, 0, decimals);
	const taxRate = totalBeforeTax > 0 ? (totalTax * 100.0) / totalBeforeTax : 0;

	const adaptedFormData: any = {
		id: null,
		status: CONSTANTS.DEFAULT_STATUS,
		scope: nullSafeGetOrElse('scope', parsedData, ''),
		scopeNonFormatted: nullSafeGetOrElse('scope', parsedData, ''),
		attachments: [],
		externalWorkOrderId: workOrder.id,

		// Single tax. `invoiceTax` seeds InvoiceFormBase's single-tax field;
		// `tax` / `taxRate` are the proposal-native mirrors used by the payload
		// builder and the parsed-vs-final comparison.
		invoiceTax: parseAmount(totalTax, 0, decimals),
		tax: parseAmount(totalTax, 0, decimals),
		taxRate: `${taxRate}`,

		// Totals: invoice-named seeds (for InvoiceFormBase mismatch + display) and
		// proposal-named mirrors (for the payload builder / comparison).
		invoiceTotalBeforeTax: totalBeforeTax,
		invoiceTotalAfterTax: totalAfterTax,
		totalBeforeTax: `${totalBeforeTax.toFixed(decimals)}`,
		totalAfterTax: `${totalAfterTax.toFixed(decimals)}`,

		laborTotalBeforeTax: initialTotal,
		laborLineItems: [],
		partsTotalBeforeTax: initialTotal,
		equipmentTotalBeforeTax: initialTotal,
		materialTotalBeforeTax: initialTotal,
		materialLineItems: [],
		travelTotalBeforeTax: initialTotal,
		travelLineItems: [],
		freightTotalBeforeTax: initialTotal,
		freightLineItems: [],
		miscTotalBeforeTax: initialTotal,
		miscLineItems: [],
		incurredTotalBeforeTax: initialTotal,
		incurredLineItems: [],

		workOrderId: workOrder.id,
		workOrder: workOrder,
		AIData: parsedData,
	};

	// Group items by category. Parser category values:
	// "labor" | "part" | "equipment" | "travel" | "freight" | "incurred" | "other"
	const groupItemsByCategory = (items: any[]) => {
		const categories: Record<string, any[]> = {
			labor: [],
			part: [],
			equipment: [],
			travel: [],
			freight: [],
			incurred: [],
			misc: [], // Default for 'other' and unknown categories
		};

		items.forEach((item) => {
			if (!item.category) {
				console.warn('Quote line item missing category, defaulting to misc:', item);
				categories.misc.push(item);
				return;
			}

			const category = item.category.toLowerCase();
			// Backend categories: labor | part | equipment | travel | freight | incurred_cost | other.
			// Map 'other' → misc and 'incurred_cost' → the 'incurred' section key.
			const normalizedCategory =
				category === 'other' ? 'misc' : category === 'incurred_cost' ? 'incurred' : category;

			if (categories.hasOwnProperty(normalizedCategory)) {
				categories[normalizedCategory].push(item);
			} else {
				console.warn(`Unknown quote category "${category}", defaulting to misc:`, item);
				categories.misc.push(item);
			}
		});

		return categories;
	};

	const createLaborItem = (item: any) => {
		const originalAmount = parseNumber(item.extended_price);
		const finalAmount = getFinalAmount(item);
		const quantity = parseNumber(item.quantity, 1);
		const unitPrice = parseNumber(item.unit_price);
		const hours = unitPrice > 0 ? originalAmount / unitPrice : quantity;
		const seconds = Math.round(hours * CONSTANTS.SECONDS_PER_HOUR);

		return {
			roleType: nullSafeGetOrElse('description', item, 'Labor'),
			numberOfTech: CONSTANTS.DEFAULT_NUM_TECH,
			hourlyRate: parseAmount(item.unit_price, 0, decimals).toFixed(decimals),
			seconds,
			amount: parseAmount(finalAmount, 0, decimals).toFixed(decimals),
			...(item.discount_amount != null && {
				discount_amount: parseAmount(item.discount_amount, 0, decimals),
			}),
		};
	};

	const createMaterialItem = (item: any, categoryHint?: 'part' | 'equipment') => {
		const description = nullSafeGetOrElse('description', item, 'Material');
		const isEquipment =
			categoryHint === 'equipment' || (categoryHint == null && isEquipmentDescription(description));
		const finalAmount = getFinalAmount(item);

		return {
			description,
			partNumber: '',
			partId: isEquipment ? null : getNextTempId(),
			equipmentId: isEquipment ? getNextTempId() : null,
			unitType: CONSTANTS.DEFAULT_UNIT_TYPE,
			unitPrice: parseAmount(item.unit_price, 0, decimals).toFixed(decimals),
			quantity: parseAmount(item.quantity, 1, decimals).toFixed(decimals),
			amount: parseAmount(finalAmount, 0, decimals).toFixed(decimals),
			...(item.discount_amount != null && {
				discount_amount: parseAmount(item.discount_amount, 0, decimals),
			}),
		};
	};

	// Simple line item for travel, freight, misc, incurred
	const createSimpleLineItem = (item: any) => ({
		description: nullSafeGetOrElse('description', item, ''),
		amount: parseAmount(getFinalAmount(item), 0, decimals).toFixed(decimals),
		...(item.discount_amount != null && {
			discount_amount: parseAmount(item.discount_amount, 0, decimals),
		}),
	});

	const lineItems = parsedData.line_items || [];
	const itemsByCategory = groupItemsByCategory(lineItems);

	adaptedFormData.laborLineItems = itemsByCategory.labor.map(createLaborItem);

	const partMaterialItems = itemsByCategory.part.map((item: any) =>
		createMaterialItem(item, 'part')
	);
	const equipmentMaterialItems = itemsByCategory.equipment.map((item: any) =>
		createMaterialItem(item, 'equipment')
	);
	adaptedFormData.materialLineItems = [...partMaterialItems, ...equipmentMaterialItems];

	adaptedFormData.travelLineItems = itemsByCategory.travel.map(createSimpleLineItem);
	adaptedFormData.freightLineItems = itemsByCategory.freight.map(createSimpleLineItem);
	adaptedFormData.miscLineItems = itemsByCategory.misc.map(createSimpleLineItem);
	adaptedFormData.incurredLineItems = itemsByCategory.incurred.map(createSimpleLineItem);

	adaptedFormData.laborTotalBeforeTax = calculateTotal(adaptedFormData.laborLineItems, decimals);
	adaptedFormData.travelTotalBeforeTax = calculateTotal(adaptedFormData.travelLineItems, decimals);
	adaptedFormData.freightTotalBeforeTax = calculateTotal(
		adaptedFormData.freightLineItems,
		decimals
	);
	adaptedFormData.miscTotalBeforeTax = calculateTotal(adaptedFormData.miscLineItems, decimals);
	adaptedFormData.incurredTotalBeforeTax = calculateTotal(
		adaptedFormData.incurredLineItems,
		decimals
	);

	const partsItems: any[] = [];
	const equipmentItems: any[] = [];
	adaptedFormData.materialLineItems.forEach((item: any) => {
		if (item.partId) partsItems.push(item);
		if (item.equipmentId) equipmentItems.push(item);
	});

	adaptedFormData.partsTotalBeforeTax = calculateTotal(partsItems, decimals);
	adaptedFormData.equipmentTotalBeforeTax = calculateTotal(equipmentItems, decimals);
	adaptedFormData.materialTotalBeforeTax = calculateTotal(
		adaptedFormData.materialLineItems,
		decimals
	);

	return adaptedFormData;
};
